import React, { useEffect, useRef, useState } from 'react';
import './HomegalleryMobile.css';

// Importing images
import health1 from '../Assets/gallery/health1.webp';
import health2 from '../Assets/gallery/health2.webp';
import health3 from '../Assets/gallery/health3.webp';
import health4 from '../Assets/gallery/health4.webp';
import const1 from '../Assets/gallery/const1.webp';
import const2 from '../Assets/gallery/const2.webp';
import const3 from '../Assets/gallery/const3.webp';
import const4 from '../Assets/gallery/const4.webp';
import it1 from '../Assets/gallery/it1.webp';
import it2 from '../Assets/gallery/it2.webp';
import it3 from '../Assets/gallery/it3.webp';

const imageData = [
  { id: 'img-1', src: health1, alt: 'Mountain Landscape' },
  { id: 'img-2', src: health2, alt: 'Forest Path' },
  { id: 'img-3', src: health3, alt: 'Lake Reflection' },
  { id: 'img-4', src: health4, alt: 'Ocean Sunset' },
  { id: 'img-6', src: const2, alt: 'Starry Night' },
  { id: 'img-7', src: it1, alt: 'Waterfall' },
  { id: 'img-8', src: const1, alt: 'Waterfall' },
  { id: 'img-9', src: it2, alt: 'Waterfall' },
  { id: 'img-10', src: it3, alt: 'Waterfall' },
  { id: 'img-11', src: const3, alt: 'Waterfall' },
  { id: 'img-12', src: const4, alt: 'Waterfall' },
];

const HomegalleryMobile = () => {
  const [currentIndex, setCurrentIndex] = useState(3);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // ✅ Update mobile/desktop mode dynamically
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Navigation
  const navigate = (direction) => {
    if (isAnimating) return;
    const nextIndex = (currentIndex + direction + imageData.length) % imageData.length;
    setCurrentIndex(nextIndex);
  };

  const goToIndex = (index) => {
    if (isAnimating || index === currentIndex) return;
    setCurrentIndex(index);
  };

  // ✅ Automatic sliding
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imageData.length);
    }, 2500); // 2.5 seconds per slide
    return () => clearInterval(interval);
  }, []);

  // ✅ Animation flag
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 600);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // ✅ Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') navigate(-1);
    if (e.key === 'ArrowRight') navigate(1);
  };

  // ✅ Touch navigation
  const touch = useRef({ startX: 0, endX: 0 });
  const handleTouchStart = (e) => {
    touch.current.startX = e.changedTouches[0].screenX;
  };
  const handleTouchEnd = (e) => {
    touch.current.endX = e.changedTouches[0].screenX;
    const delta = touch.current.startX - touch.current.endX;
    if (Math.abs(delta) > 30) {
      navigate(delta > 0 ? 1 : -1);
    }
  };

  return (
    <section className="cfw-section">
      <div className="cfw-wrapper">
        <div className="cfw-info">
          <h2>A Journey of Innovation, <br />Captured in Every Frame.</h2>
          <p>A Visual Symphony of Our Journey from 2007 to Now.</p>
        </div>

        <div
          className="cfw-container"
          ref={containerRef}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="cfw-coverflow">
            {imageData.map((img, index) => {
              if (isMobile) {
                // ✅ MOBILE: show only one centered image
                return (
                  index === currentIndex && (
                    <div key={index} className="cfw-item active">
                      <div className="cfw-cover">
                        <img src={img.src} alt={img.alt} loading="lazy" />
                      </div>
                    </div>
                  )
                );
              } else {
                // 💻 DESKTOP: keep 3D coverflow
                let offset = index - currentIndex;
                if (offset > imageData.length / 2) offset -= imageData.length;
                if (offset < -imageData.length / 2) offset += imageData.length;

                const absOffset = Math.abs(offset);
                const sign = Math.sign(offset);
                const translateX = offset * 220;
                const translateZ = -absOffset * 200;
                const rotateY = -sign * Math.min(absOffset * 60, 60);
                const opacity = absOffset > 3 ? 0 : 1 - absOffset * 0.2;
                const scale = 1 - absOffset * 0.1;
                const zIndex = 100 - absOffset;

                return (
                  <div
                    key={index}
                    className={`cfw-item ${index === currentIndex ? 'active' : ''}`}
                    style={{
                      transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                      opacity,
                      zIndex,
                    }}
                    onClick={() => goToIndex(index)}
                  >
                    <div className="cfw-cover">
                      <img src={img.src} alt={img.alt} loading="lazy" />
                    </div>
                    <div
                      className="cfw-reflection"
                      style={{ backgroundImage: `url(${img.src})` }}
                    />
                  </div>
                );
              }
            })}
          </div>

          {/* ✅ Show navigation & dots below on mobile */}
          <div className="cfw-mobile-controls">
            <button className="cfw-nav-btn cfw-prev" onClick={() => navigate(-1)}>‹</button>
            <button className="cfw-nav-btn cfw-next" onClick={() => navigate(1)}>›</button>
            <div className="cfw-dots">
              {imageData.map((_, index) => (
                <div
                  key={index}
                  className={`cfw-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomegalleryMobile;
